import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { environment } from '@env';
import { HttpClientService } from './http-client.service';
import { PersistenceService } from './persistence.service';
import { NavigationService } from './navigation.service';
import { TranslationService } from './translation.service';
import { UserEventLoggerService } from './user-event-logger.service';
import { KioskSetupService } from '@app/setup/services/kiosk-setup.service';
import { ErrorService } from '@app/shared/services/error.service';

export type AuthErrors = 'emailNotFound' | 'userHasNoPermission' | 'unknown';
export type AuthResponse = { email: string; language: string };

@Injectable({ providedIn: 'root' })
export class AuthService {
  adminInfo: { userId?: string; orgId?: string; email?: string } = {};
  private isAuthenticated: boolean;
  private accessToken: string;
  private refreshToken: string;
  private sessionHasBeenKilled: boolean = false;

  constructor(
    private httpClient: HttpClientService,
    private persistenceService: PersistenceService,
    private navigationService: NavigationService,
    private translationService: TranslationService,
    private userEventLogger: UserEventLoggerService,
    private kioskSetUpService: KioskSetupService,
    private errorService: ErrorService
  ) { }

  async signInKiosk(rawEmail: string): Promise<{ response?: AuthResponse; error?: AuthErrors }> {
    try {
      const email = rawEmail.trim().toLowerCase();
      const response = await this.httpClient.postWithoutAuth<AuthResponse>(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/sign-in`, {
        email,
      });
      this.adminInfo.email = response.email;
      this.translationService.setLanguage(response.language);
      return { response };
    } catch (e) {
      if (e.status === HttpStatusCode.NotFound) {
        return { error: 'emailNotFound' };
      }
      if (e.status === HttpStatusCode.Forbidden) {
        return { error: 'userHasNoPermission' };
      }
      return { error: 'unknown' };
    }
  }

  async sendCodeAgain(): Promise<void> {
    const bodyData = { email: this.adminInfo.email };

    await this.httpClient.postWithoutAuth(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/resend-otp-code`, bodyData);
  }

  async getAuthInfo(kioskSessionId: string): Promise<{
    token: string;
    kioskSessionId: string;
  } | null> {
    const response = await this.httpClient.postWithoutAuth<{
      token: string;
      kioskSessionId: string;
    }>(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/auth-info`, { kioskSessionId });
    return response || null;
  }

  async signIn2FA(hashValue: string) {
    try {
      const requestBody = { email: this.adminInfo.email, hashValue };
      const response = await this.httpClient.postWithCredentials<{
        accessToken: string;
        refreshToken: string;
        userId: string;
        sessionNames: string[];
      }>(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/complete-2fa-sign-in`, requestBody);
      this.kioskSetUpService.sessionNames = response.sessionNames;
      this.persistenceService.setItem('sessionNames', JSON.stringify(response.sessionNames));

      this.isAuthenticated = true;
      this.accessToken = `Bearer ${response.accessToken}`;
      this.refreshToken = response.refreshToken;
      this.adminInfo.userId = response.userId;
      this.persistenceService.setItem('userId', response.userId);
      this.persistenceService.setItem('refresh_token', this.refreshToken);
      this.persistenceService.setItem('accessToken', response.accessToken);
      this.persistenceService.clearItem('locationId');
      this.persistenceService.clearItem('sessionId');
      this.persistenceService.clearItem('selectedEntryMethod');
      await this.userEventLogger.initialize();
      this.userEventLogger.logEvent('VALID_2FA');
      return { response };
    } catch (error) {
      if (error.error === 'Not Found') {
        return { error: 'incorrectCode' };
      }
      if (error.error === 'Unauthorized') {
        return { error: 'noTrialsLeft' };
      }
      return { error };
    }
  }

  async signInStorage() {
    const userInfo: Record<string, string> = {};
    try {
      const response = await this.httpClient.getWithCredentials<{
        accessToken: string;
        language: string;
        userId: string;
      }>(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/sign-in-cookie`);
      userInfo.userId = response.userId;
      userInfo.language = response.language;
      userInfo.accessToken = response.accessToken;
      this.persistenceService.setItem('userId', response.userId);
      this.persistenceService.setItem('language', response.language);
      this.persistenceService.setItem('accessToken', response.accessToken);
    } catch (e) {
      const accessTokenInLocalStorage = this.persistenceService.getItem('accessToken');
      const cookieExistedButWasDeleted = accessTokenInLocalStorage !== undefined;
      if (!cookieExistedButWasDeleted) {
        return false;
      }
      userInfo.userId = this.persistenceService.getItem('userId');
      userInfo.language = this.persistenceService.getItem('language');
      userInfo.accessToken = this.persistenceService.getItem('accessToken');
    }
    userInfo.userId = this.persistenceService.getItem('userId');
    userInfo.language = this.persistenceService.getItem('language');
    userInfo.accessToken = this.persistenceService.getItem('accessToken');
    this.isAuthenticated = true;
    this.adminInfo = { userId: userInfo.userId };
    this.accessToken = `Bearer ${userInfo.accessToken}`;
    this.translationService.setLanguage(userInfo.language);
    return true;
  }

  async getIsAuthenticated() {
    if (this.isAuthenticated !== undefined) {
      return this.isAuthenticated;
    }

    try {
      const success = await this.signInStorage();
      return success;
    } catch (e) {
      return false;
    }
  }

  async killKioskSession() {
    this.sessionHasBeenKilled = true;
    this.navigationService.goToLoggedOut();

    this.adminInfo = {};
    this.isAuthenticated = false;
    this.persistenceService.clearAllData();
  }

  async redirectToLogIn() {
    this.adminInfo = {};
    this.isAuthenticated = false;
    this.navigationService.goToLogIn();
  }

  getAdminEmail(): string {
    return this.adminInfo?.email;
  }

  getAuthHeader() {
    return `Bearer ${this.persistenceService.getItem('accessToken')}`;
  }

  /**
   * Returns the JWT exp claim (seconds since epoch) from the stored access token, or null if missing/invalid.
   */
  getAccessTokenExpirationSeconds(): number | null {
    const accessToken = this.persistenceService.getItem('accessToken');
    if (!accessToken || typeof accessToken !== 'string') {
      return null;
    }
    const raw = accessToken.startsWith('Bearer ') ? accessToken.slice(7) : accessToken;
    const parts = raw.split('.');
    if (parts.length !== 3) {
      return null;
    }
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  checkLessThan3MonthsToExpire(exp: number) {
    const THREE_MONTHS_SEC = 3 * 30 * 24 * 60 * 60;
    const now = Date.now() / 1000;
    return exp < now + THREE_MONTHS_SEC;
  }

  async refreshAccessToken() {
    try {
      const exp = this.getAccessTokenExpirationSeconds();
      const kioskSessionId = this.persistenceService.getItem('sessionId');
      const accessToken = this.persistenceService.getItem('accessToken');

      if (exp && kioskSessionId && accessToken && this.checkLessThan3MonthsToExpire(exp)) {
        const response = await this.httpClient.post<{
          accessToken?: string;
        }>(`${environment.PEOPLE_CLOUD_APP_URL}/auth/kiosk/refresh-token`, { token: accessToken, kioskSessionId }, this.getAuthHeader());
        if (response?.accessToken) {
          this.persistenceService.setItem('accessToken', response.accessToken);
          this.persistenceService.setItem('sessionId', kioskSessionId);
        }
      }
    } catch (error) {
      this.errorService.logErrorForDevelopers(error);
    }
  }

  getSessionHasBeenKilled() {
    const wasKilled = this.sessionHasBeenKilled;
    this.sessionHasBeenKilled = false;
    return wasKilled;
  }
}
